import { useEffect, useMemo, useState, type JSX } from "react";
import { useNavigate, useParams } from "react-router";
import { useLiveQuery } from "dexie-react-hooks";
import { addCalendarDays, buildSlotIndex, deriveTodayDigest, localDateInZone } from "@recipevault/shared";
import { Button } from "@/ui/button.js";
import { db } from "../../sync/db.js";
import { useHouseholdTimezone } from "../auth/useHouseholdTimezone.js";
import { useMe } from "../auth/useMe.js";
import { useMealPlan } from "./useMealPlan.js";
import { LeadTimesCard } from "./LeadTimesCard.js";
import { WeekStrip } from "./WeekStrip.js";
import { DayView } from "./DayView.js";
import { TaskList } from "./TaskList.js";
import { DuplicateWeekSheet } from "./DuplicateWeekSheet.js";
import { BuildShoppingListSheet } from "./BuildShoppingListSheet.js";
import { useSwipeDay } from "./useSwipeDay.js";

/** Whether `date` falls within the 7-day window starting at `weekStart` (inclusive). */
function isInWeek(date: string, weekStart: string): boolean {
  return date >= weekStart && date <= addCalendarDays(weekStart, 6);
}

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "UTC" });

/** Full weekday name for an ISO calendar date, anchored at UTC noon (no DST edge) — mirrors
 * WeekStrip's `weekdayLetter` pattern. Never `new Date(isoDate)`, which parses as local
 * midnight and can shift the displayed day. */
function weekdayName(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map((v) => Number.parseInt(v, 10)) as [number, number, number];
  return WEEKDAY_FORMATTER.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

/**
 * Day-pager week view: a `WeekStrip` of tappable day chips above a single day's
 * `DayView`, that day's tasks, and a collapsed "This week" section for week-scoped
 * concerns (critical lead times, duplicate/build-list actions, and the full task list).
 * Renders whatever `useMealPlan` returns (a real row, or a `buildEmptyWeek` placeholder)
 * without ever writing to Dexie itself — but `DayView`'s slot editor (via `SlotSheet`) DOES
 * write, through `editWeek` (lazy-create + edit-by-position), which always resolves against
 * the real Dexie row regardless of what this component happened to be rendering.
 */
export function PlanWeek(): JSX.Element {
  const { weekStart } = useParams<{ weekStart: string }>();
  const navigate = useNavigate();
  const plan = useMealPlan(weekStart ?? "");
  const recipes = useLiveQuery(() => db.recipes.toArray(), []);
  // The REAL Dexie row (undefined until one exists) — distinct from `plan`, which falls
  // back to a synthesized empty-week shell. Drives the "Duplicate a prior week" trigger:
  // only offered while this week has no plan of its own yet.
  const realPlan = useLiveQuery(() => db.mealPlans.get(weekStart ?? ""), [weekStart]);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [buildListOpen, setBuildListOpen] = useState(false);
  const [weekSectionOpen, setWeekSectionOpen] = useState(false);
  const tz = useHouseholdTimezone();
  const { me } = useMe();
  const householdId = me?.user.currentHouseholdId ?? me?.memberships[0]?.householdId ?? "";

  // Real "now", not frozen — matches how the email worker derives the digest server-side.
  const today = useMemo(() => localDateInZone(tz, new Date()), [tz]);
  const digest = useMemo(() => deriveTodayDigest(plan, today), [plan, today]);
  const slotIndex = useMemo(() => buildSlotIndex(plan), [plan]);

  const recipeTitles = useMemo(() => {
    const map = new Map<string, string>();
    for (const recipe of recipes ?? []) map.set(recipe.id, recipe.title);
    return map;
  }, [recipes]);

  const recipesById = useMemo(
    () => new Map((recipes ?? []).map((r) => [r.id, r])),
    [recipes],
  );

  const safeWeekStart = weekStart ?? "";

  const [selectedDate, setSelectedDate] = useState(() =>
    isInWeek(today, safeWeekStart) ? today : safeWeekStart,
  );

  // Reset the selection whenever the WEEK changes (chevron / swipe-past-edge navigation) —
  // deliberately NOT depending on `today` too: a midnight tick while the page sits open
  // must never yank the user's selection away from a day they tapped earlier that day.
  useEffect(() => {
    setSelectedDate(isInWeek(today, safeWeekStart) ? today : safeWeekStart);
    // Deps intentionally exclude `today` and `safeWeekStart` — see comment above.
  }, [weekStart]);

  const dayTasks = useMemo(
    () => plan.tasks.filter((t) => t.doOnDate === selectedDate),
    [plan.tasks, selectedDate],
  );

  // The day-tasks section shows the SELECTED day's tasks, which the strip/swipe can move to
  // any day — so the heading must track it, not hard-read "Today's tasks".
  const dayTasksLabel =
    selectedDate === today ? "Today's tasks" : `${weekdayName(selectedDate)}'s tasks`;

  const selectedDay = plan.days.find((d) => d.date === selectedDate) ?? plan.days[0];

  function goToPrevDay(): void {
    if (selectedDate > safeWeekStart) {
      setSelectedDate(addCalendarDays(selectedDate, -1));
    } else {
      void navigate(`/plan/${addCalendarDays(safeWeekStart, -7)}`);
    }
  }

  function goToNextDay(): void {
    if (selectedDate < addCalendarDays(safeWeekStart, 6)) {
      setSelectedDate(addCalendarDays(selectedDate, 1));
    } else {
      void navigate(`/plan/${addCalendarDays(safeWeekStart, 7)}`);
    }
  }

  const swipeHandlers = useSwipeDay(goToPrevDay, goToNextDay);

  if (!weekStart || !selectedDay) {
    return <p className="text-destructive text-sm">Missing week.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">
          Week of {plan.weekStartDate}
        </h1>
      </header>

      <DuplicateWeekSheet open={duplicateOpen} onOpenChange={setDuplicateOpen} targetWeekStart={weekStart} />
      <BuildShoppingListSheet open={buildListOpen} onOpenChange={setBuildListOpen} plan={plan} />

      <WeekStrip
        weekStart={weekStart}
        selectedDate={selectedDate}
        today={today}
        days={plan.days}
        onSelect={setSelectedDate}
        onWeekChange={(nw) => {
          void navigate(`/plan/${nw}`);
        }}
      />

      <div data-testid="day-pager-swipe-area" className="touch-pan-y" {...swipeHandlers}>
        <DayView
          day={selectedDay}
          isToday={selectedDay.date === today}
          recipeTitles={recipeTitles}
          weekStart={weekStart}
          days={plan.days}
          slotIndex={slotIndex}
          recipesById={recipesById}
          householdId={householdId}
        />
      </div>

      <TaskList weekStart={weekStart} tasks={dayTasks} label={dayTasksLabel} />

      <div className="flex flex-col gap-3">
        <button
          type="button"
          aria-expanded={weekSectionOpen}
          onClick={() => {
            setWeekSectionOpen((v) => !v);
          }}
          className="flex min-h-12 w-full items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-left"
        >
          <h2 className="text-sm font-semibold text-zinc-200">This week</h2>
          <span className="text-xs text-zinc-500">{weekSectionOpen ? "Hide" : "Show"}</span>
        </button>

        {weekSectionOpen && (
          <div className="flex flex-col gap-4">
            <LeadTimesCard comingUp={digest.comingUp} />

            <div className="flex items-center gap-2">
              {realPlan === undefined && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDuplicateOpen(true);
                  }}
                >
                  Duplicate a prior week
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBuildListOpen(true);
                }}
              >
                Build shopping list
              </Button>
            </div>

            <TaskList weekStart={weekStart} tasks={plan.tasks} label="This week's tasks" />
          </div>
        )}
      </div>
    </div>
  );
}
